import { Injectable, signal } from '@angular/core';
import { environment } from '@nrwl/environment';
import { FormTemplateType } from '@nrwl/rx-models';

export enum FeatureKey {
  UNIFIED_CHARTING = 'UNIFIED_CHARTING',
  AI_SCRIBE = 'AI_SCRIBE',
  AI_SCRIBE_STANDALONE = 'AI_SCRIBE_STANDALONE',
  AI_SCRIBE_STANDALONE_ADDON = 'AI_SCRIBE_STANDALONE_ADDON',
  FORM_AMENDMENTS = 'FORM_AMENDMENTS',
  INTERCOM_WIDGET = 'INTERCOM_WIDGET',
  PERSONALIZED_PLANS = 'PERSONALIZED_PLANS',
}

export const filterPersonalizedPlanTemplates = <T extends { type?: number | string | null }>(
  templates: readonly T[] | null | undefined,
  flags: ReadonlySet<FeatureKey>,
): T[] =>
  (templates ?? []).filter(
    (template) =>
      flags.has(FeatureKey.PERSONALIZED_PLANS) ||
      Number(template.type) !== FormTemplateType.PersonalizedPlan,
  );

type AddonLike = { addon?: string; is_expired?: boolean | string | number | null };
export type LaunchDarklyFlagResult = { value?: unknown; defaultUsed?: boolean };
export type LaunchDarklyFlagResults = Record<string, LaunchDarklyFlagResult>;
export interface LaunchDarklySyncState {
  contextKey: string | null;
  resolved: boolean;
}
export interface LaunchDarklyContextIdentity {
  id?: string | number | null;
  company?: string | number | null;
  clinic?: {
    id?: string | number | null;
  } | null;
}

export const createLaunchDarklyContextKey = (
  identity: LaunchDarklyContextIdentity | null | undefined,
): string | null => {
  if (identity?.id === undefined || identity.id === null) {
    return null;
  }

  return JSON.stringify({
    userId: String(identity.id),
    companyId:
      identity.company === undefined || identity.company === null ? null : String(identity.company),
    clinicId:
      identity.clinic?.id === undefined || identity.clinic.id === null
        ? null
        : String(identity.clinic.id),
  });
};

const STORAGE_KEY = environment.localStorage.featureFlags;
const STORAGE_COMPANY_KEY = environment.localStorage.companyId;

const addonToFlags: Record<string, FeatureKey[]> = {
  UNIFIED_CHARTING: [FeatureKey.UNIFIED_CHARTING],
  // The CPR addon is a permanent entitlement and is intentionally separate from the LD rollout flag.
  AI_SCRIBE_STANDALONE: [FeatureKey.AI_SCRIBE_STANDALONE_ADDON],
};

const launchDarklyToFlags: Record<string, FeatureKey[]> = {
  'ai-scribe': [FeatureKey.AI_SCRIBE],
  'ai-scribe-standalone': [FeatureKey.AI_SCRIBE_STANDALONE],
  'form-amendments': [FeatureKey.FORM_AMENDMENTS],
  'intercom-widget': [FeatureKey.INTERCOM_WIDGET],
  'personalized-plans': [FeatureKey.PERSONALIZED_PLANS],
};

@Injectable({ providedIn: 'root' })
export class FeatureFlagsStore {
  private readonly storage: Storage | null = this.getStorage();
  private readonly flags = signal<Set<FeatureKey>>(new Set());
  private readonly launchDarklySyncState = signal<LaunchDarklySyncState>({
    contextKey: null,
    resolved: false,
  });

  readonly flagsSignal = this.flags.asReadonly();
  readonly launchDarklySyncStateSignal = this.launchDarklySyncState.asReadonly();

  constructor() {
    this.hydrateFromStorage();
  }

  setFromAddons(addons: AddonLike[] | null | undefined): void {
    const normalized = Array.isArray(addons) ? addons : [];
    const next = new Set<FeatureKey>(this.flags());

    Object.values(addonToFlags)
      .flat()
      .forEach((flag) => next.delete(flag));

    normalized.forEach((addon) => {
      const featureKeys = addonToFlags[addon?.addon ?? ''];
      if (!featureKeys) {
        return;
      }

      if (!this.isAddonActive(addon)) {
        return;
      }

      featureKeys.forEach((flag) => next.add(flag));
    });

    this.flags.set(next);
    this.persist(next);
  }

  beginLaunchDarklySync(contextKey: string): void {
    const next = this.withoutLaunchDarklyFlags();

    this.flags.set(next);
    this.launchDarklySyncState.set({ contextKey, resolved: false });
    this.persist(next);
  }

  setFromLaunchDarkly(
    results: LaunchDarklyFlagResults | null | undefined,
    contextKey?: string,
  ): void {
    const currentSyncState = this.launchDarklySyncState();

    if (contextKey !== undefined && contextKey !== currentSyncState.contextKey) {
      return;
    }

    const next = this.withoutLaunchDarklyFlags();

    const applyLaunchDarklyResult = ([key, result]: [string, LaunchDarklyFlagResult]): void => {
      const featureKeys = launchDarklyToFlags[key];
      if (!featureKeys) {
        return;
      }

      if (!this.isLaunchDarklyFlagActive(result)) {
        return;
      }

      featureKeys.forEach((flag) => next.add(flag));
    };

    Object.entries(results ?? {}).forEach(applyLaunchDarklyResult);

    this.flags.set(next);
    this.launchDarklySyncState.set({
      contextKey: contextKey ?? currentSyncState.contextKey,
      resolved: true,
    });
    this.persist(next);
  }

  resetLaunchDarkly(): void {
    const next = this.withoutLaunchDarklyFlags();

    this.flags.set(next);
    this.launchDarklySyncState.set({ contextKey: null, resolved: false });
    this.persist(next);
  }

  reset(): void {
    this.flags.set(new Set());
    this.launchDarklySyncState.set({ contextKey: null, resolved: false });
    this.clearStorage();
  }

  has(flag: FeatureKey): boolean {
    return this.flags().has(flag);
  }

  private hydrateFromStorage(): void {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw) as { companyId?: string; flags?: FeatureKey[] } | FeatureKey[];

      if (Array.isArray(parsed)) {
        // Backward compatibility with the old format
        const next = new Set<FeatureKey>();
        parsed.forEach((value) => {
          if (Object.values(FeatureKey).includes(value as FeatureKey)) {
            next.add(value as FeatureKey);
          }
        });
        this.flags.set(next);
        return;
      }

      const currentCompanyId = this.storage?.getItem(STORAGE_COMPANY_KEY) ?? undefined;
      if (
        !parsed?.companyId ||
        parsed.companyId !== currentCompanyId ||
        !Array.isArray(parsed.flags)
      ) {
        this.clearStorage();
        return;
      }

      const next = new Set<FeatureKey>();
      parsed.flags.forEach((value) => {
        if (Object.values(FeatureKey).includes(value as FeatureKey)) {
          next.add(value as FeatureKey);
        }
      });

      this.flags.set(next);
    } catch {
      this.clearStorage();
    }
  }

  private persist(flags: Set<FeatureKey>): void {
    const companyId = this.storage?.getItem(STORAGE_COMPANY_KEY) ?? undefined;
    this.storage?.setItem(STORAGE_KEY, JSON.stringify({ companyId, flags: Array.from(flags) }));
  }

  private withoutLaunchDarklyFlags(): Set<FeatureKey> {
    const next = new Set<FeatureKey>(this.flags());

    Object.values(launchDarklyToFlags)
      .flat()
      .forEach((flag) => next.delete(flag));

    return next;
  }

  private clearStorage(): void {
    this.storage?.removeItem(STORAGE_KEY);
  }

  private getStorage(): Storage | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage;
    } catch {
      return null;
    }
  }

  private isAddonActive(addon: AddonLike): boolean {
    const value = addon?.is_expired;

    if (value === undefined || value === null) {
      return true;
    }

    if (typeof value === 'boolean') {
      return !value;
    }

    if (typeof value === 'number') {
      return value === 0;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes'].includes(normalized)) {
        return false;
      }

      if (['', 'false', '0', 'no'].includes(normalized)) {
        return true;
      }

      return true;
    }

    return false;
  }

  private isLaunchDarklyFlagActive(result: LaunchDarklyFlagResult): boolean {
    return result?.value === true;
  }
}

export const addonToFeatureFlagsMap = addonToFlags;
export const launchDarklyToFeatureFlagsMap = launchDarklyToFlags;
